import { computed, type ComputedRef, type Ref, ref } from 'vue'

/**
 * The two locales this product ships. `dv` (Dhivehi, Thaana script) is the
 * portal's default; `en` is the back office's.
 */
export type UiLocale = 'en' | 'dv'

/** Text direction implied by a locale. Thaana is right-to-left. */
export type UiDirection = 'ltr' | 'rtl'

export const UI_LOCALES: readonly UiLocale[] = ['en', 'dv'] as const

/**
 * Module-level state, deliberately.
 *
 * Formatting helpers are plain functions callable from anywhere — a store, a
 * table's cell formatter, a CSV export — not only from inside a component.
 * They need the current locale without a Vue injection context, so the locale
 * lives in a module singleton and `useLocale()` is a thin reactive view of it.
 */
const locale = ref<UiLocale>('en')

export function directionFor(value: UiLocale): UiDirection {
  return value === 'dv' ? 'rtl' : 'ltr'
}

/**
 * The BCP-47 chain handed to `Intl`.
 *
 * An array rather than a single tag because Dhivehi has patchy ICU coverage:
 * passing `['dv', 'en']` lets the runtime fall back to English formatting
 * instead of throwing or silently producing the root locale's output. Digits
 * are Latin in both, so a fallback is cosmetically invisible for numbers.
 */
export function intlLocales(value: UiLocale = locale.value): string[] {
  return value === 'dv' ? ['dv-MV', 'dv', 'en-MV', 'en'] : ['en-MV', 'en']
}

/** Current locale, readable from non-component code. */
export function currentLocale(): UiLocale {
  return locale.value
}

/**
 * Sets the active locale and reflects it onto `<html>`.
 *
 * Both attributes matter: `lang` switches the Thaana font stack and the looser
 * line height in tokens.css, and `dir` is what flips every logical property in
 * every component at once. That is the whole RTL strategy — one attribute,
 * because the components never say `margin-left`.
 */
export function setLocale(value: UiLocale): void {
  locale.value = value
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.setAttribute('lang', value)
    root.setAttribute('dir', directionFor(value))
  }
}

export interface UseLocaleReturn {
  /** The active locale. Writable; writing it runs `setLocale`. */
  locale: Ref<UiLocale>
  /** `rtl` for Dhivehi, `ltr` for English. */
  dir: ComputedRef<UiDirection>
  /** True when the active locale is right-to-left. */
  isRtl: ComputedRef<boolean>
  /** The `Intl` locale chain for the active locale. */
  intlLocales: ComputedRef<string[]>
  setLocale: (value: UiLocale) => void
  /** Picks the field matching the active locale from a `{ en, dv }` pair. */
  pick: <T>(values: { en: T; dv: T }) => T
}

export function useLocale(): UseLocaleReturn {
  const writable = computed<UiLocale>({
    get: () => locale.value,
    set: (value) => setLocale(value),
  })

  return {
    locale: writable,
    dir: computed(() => directionFor(locale.value)),
    isRtl: computed(() => directionFor(locale.value) === 'rtl'),
    intlLocales: computed(() => intlLocales(locale.value)),
    setLocale,
    pick: <T>(values: { en: T; dv: T }) => (locale.value === 'dv' ? values.dv : values.en),
  }
}
